import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import {
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Percent,
  DollarSign,
} from "lucide-react";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { memo } from "react";
import type { CommissionRateResponseDTO } from "../types/commission-rate.types";
import { RATE_TYPE_LABELS } from "./CommissionRateFormDialog";

interface CommissionRatesTableProps {
  commissionRates: CommissionRateResponseDTO[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onEdit: (commissionRate: CommissionRateResponseDTO) => void;
  onDelete: (commissionRate: CommissionRateResponseDTO) => void;
  onPageChange: (page: number) => void;
}

const formatId = (id: number): string => `COM${String(id).padStart(3, "0")}`;

const deriveName = (cr: CommissionRateResponseDTO): string => {
  const chargeLabel = cr.rate !== null ? "Percentual" : "Fixa";
  const typeLabel = RATE_TYPE_LABELS[cr.type] ?? cr.type;
  return `Comissão ${chargeLabel} ${typeLabel}`;
};

const formatValue = (cr: CommissionRateResponseDTO): string => {
  if (cr.rate !== null) {
    const pct = cr.rate * 100;
    return `${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1)}%`;
  }
  if (cr.value !== null) {
    return cr.value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }
  return "-";
};

export const CommissionRatesTable = memo(function CommissionRatesTable({
  commissionRates,
  loading,
  currentPage,
  totalPages,
  onEdit,
  onDelete,
  onPageChange,
}: CommissionRatesTableProps) {
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Valor</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {commissionRates.map((cr) => (
            <TableRow key={cr.id}>
              <TableCell className="font-mono text-muted-foreground text-xs">
                {formatId(cr.id)}
              </TableCell>
              <TableCell className="font-medium">
                {cr.name ?? deriveName(cr)}
              </TableCell>
              <TableCell>
                {cr.rate !== null ? (
                  <Badge variant="secondary" className="gap-1">
                    <Percent className="w-3 h-3" />
                    Percentual
                  </Badge>
                ) : (
                  <Badge variant="default" className="gap-1">
                    <DollarSign className="w-3 h-3" />
                    Fixo
                  </Badge>
                )}
              </TableCell>
              <TableCell>{formatValue(cr)}</TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Can permission={ACTIONS.commissionRates.update}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onEdit(cr)}
                      title="Editar"
                      disabled={loading}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </Can>
                  <Can permission={ACTIONS.commissionRates.delete}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => onDelete(cr)}
                      title="Excluir"
                      disabled={loading}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </Can>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
          <div className="text-sm text-muted-foreground">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-2 items-center flex-wrap justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>

            <div className="flex gap-1 flex-wrap justify-center">
              {Array.from({ length: totalPages }, (_, i) => {
                const showPage =
                  i === 0 ||
                  i === totalPages - 1 ||
                  (i >= currentPage - 1 && i <= currentPage + 1);

                const showEllipsis =
                  (i === 1 && currentPage > 2) ||
                  (i === totalPages - 2 && currentPage < totalPages - 3);

                if (showEllipsis) {
                  return (
                    <span
                      key={i}
                      className="px-2 py-1 text-sm text-muted-foreground"
                    >
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <Button
                    key={i}
                    variant={currentPage === i ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(i)}
                    disabled={loading}
                    className="min-w-10"
                  >
                    {i + 1}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
});
